import {useEffect, useState} from 'react'
import {AccommodationPageNavSections} from 'modules/accommodation/types'

type Props = {
  /**
   * Amount of pixels from top of the viewport where section will be marked as intersecting
   */
  intersectWithSectionAt: number
  /**
   * If should skip observing
   */
  shouldSkip: boolean
}

const INTERSECTION_OFFSET = 10

/**
 * Observes sections defined by the selector (static atm, only used for sections on AP)
 * and returns name of the section which is currently
 * intersecting with the point given by intersectWithSectionAt param
 */

export const useGetActiveTabFromIntersectingSection = ({
  intersectWithSectionAt,
  shouldSkip
}: Props) => {
  const [activeTab, setActiveTab] = useState<string>('')
  useEffect(() => {
    //moves intersection point slightly lower than the bottom of the nav-bar
    const bottomOffset =
      window.innerHeight - intersectWithSectionAt - INTERSECTION_OFFSET
    const topOffset = intersectWithSectionAt + INTERSECTION_OFFSET

    const observerOptions = {
      rootMargin: `-${topOffset}px 0px -${bottomOffset}px 0px`,
      threshold: 0
    }

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      const firstIntersectingEntry = entries.find(entry => entry.isIntersecting)

      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = firstIntersectingEntry.target as HTMLElement
          setActiveTab(element.dataset.scrollname)
        }
      })
    }

    const hasNativeIntersectionObserver =
      typeof IntersectionObserver !== 'undefined'

    if (hasNativeIntersectionObserver && !shouldSkip) {
      const observer = new IntersectionObserver(
        observerCallback,
        observerOptions
      )

      document
        .querySelectorAll('[data-scrollname]')
        .forEach((element: HTMLElement) => {
          if (
            element &&
            Object.values(AccommodationPageNavSections).includes(
              element.dataset.scrollname as AccommodationPageNavSections
            )
          ) {
            observer.observe(element)
          }
        })

      return () => {
        observer.disconnect()
      }
    }
  }, [intersectWithSectionAt, shouldSkip])

  return {activeTab}
}
